import React, { useState } from "react";
import SettleSheet from "./SettleSheet";
import { usePayBill, billAmountText } from "./PaidAmountSheet";
import { payoffText } from "../../utils/loans";
import { CheckCircle, BarChart2, List, Target } from "react-feather";
import { useTheme } from "../../theme/ThemeContext";
import { useSettings } from "../../settings/SettingsContext";
import { isInPeriod } from "../../utils/period";
import { needsConfirmation } from "../../utils/autopay";
import { daysUntilDue, groupUnpaid } from "../../utils/bills";
import { Card, SectionTitle, Empty, LinkButton, useMoney, formatDate } from "../ui";
import ExpenseAnalysis from "../../analysis/ExpenseAnalysis";
import { monthSummary } from "../../analysis/monthSummary";
import { PlannedBudgets, oneTimeBudgetsOf } from "../../analysis/Budgets";

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
};

const dueText = (days) => {
  if (days < 0) return `Due · ${Math.abs(days)} day${days === -1 ? "" : "s"} late`;
  if (days === 0) return "Due today";
  if (days === 1) return "Due tomorrow";
  return `${days} days left`;
};

const daysSince = (iso) => {
  const d = -daysUntilDue(iso);
  return d <= 0 ? "today" : d === 1 ? "1 day ago" : `${d} days ago`;
};

const HomeScreen = ({ data, goTo }) => {
  const { tokens, fonts } = useTheme();
  const { settings } = useSettings();
  const money = useMoney();
  const { bills, income, loans, cards, assets } = data;

  const totalAssets = assets.reduce((s, a) => s + a.value, 0);
  const totalOwed = loans.reduce((s, l) => s + l.remainingBalance, 0);
  const netWorth = totalAssets - totalOwed;

  const monthIncome = income.filter((i) => isInPeriod(i.date, "Month"));
  const monthBills = bills.filter((b) => isInPeriod(b.dueDate, "Month"));
  const received = monthIncome.filter((i) => i.status === "Received").reduce((s, i) => s + i.amount, 0);
  const incoming = monthIncome.filter((i) => i.status !== "Received").reduce((s, i) => s + i.amount, 0);
  const paidOut = monthBills.filter((b) => b.status === "Paid").reduce((s, b) => s + b.amount, 0);
  const toPay = monthBills.filter((b) => b.status !== "Paid").reduce((s, b) => s + b.amount, 0);

  const confirmations = settings.autopayReminders ? bills.filter((b) => needsConfirmation(b)) : [];
  const dueSoon = bills
    .filter((b) => b.status === "Pending" && !confirmations.includes(b) && daysUntilDue(b.dueDate) <= 7)
    .sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1));

  // For the first week of a month, a look back at the one that just ended.
  const today = new Date();
  const prev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const summary = today.getDate() <= 7 ? monthSummary(bills, income, prev.getFullYear(), prev.getMonth()) : null;
  const lastMonth = summary && (summary.spent > 0 || summary.received > 0) ? summary : null;

  // A bill whose amount changes each time asks for what was actually paid.
  const payBill = usePayBill(data);
  const markPaid = payBill.pay;
  const dueGroups = groupUnpaid(dueSoon);

  // Money lent that hasn't come back yet, and money borrowed not yet paid back.
  const owedToMe = bills.filter((b) => b.type === "Lent" && !b.repaidIncomeId).sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1));
  const owedTotal = owedToMe.reduce((s, b) => s + b.amount, 0);
  const iOwe = income.filter((i) => i.type === "Borrowed" && i.status === "Received" && !i.repaidBillId).sort((a, b) => (a.date < b.date ? -1 : 1));
  const iOweTotal = iOwe.reduce((s, i) => s + i.amount, 0);
  const [settling, setSettling] = useState(null); // { kind: "lent" | "borrowed", entry }

  // One row per bill; several unpaid ones of the same bill are paid together.
  const DueGroupRow = ({ group, first }) => {
    const [oldest] = group.items;
    const count = group.items.length;
    const varies = group.items.some((b) => b.variableAmount);
    const note =
      count === 1
        ? `${group.type} · ${dueText(daysUntilDue(oldest.dueDate))} · ${formatDate(oldest.dueDate)}`
        : `${group.type} · ${count} unpaid · oldest ${formatDate(oldest.dueDate)} (${dueText(daysUntilDue(oldest.dueDate)).toLowerCase()})`;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderTop: first ? "none" : `1px solid ${tokens.surfaceBorder}` }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: tokens.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{group.payee}</div>
          <div style={{ fontSize: 12, color: count > 1 ? tokens.negative : tokens.textMuted, marginTop: 2 }}>{note}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontFamily: fonts.mono, fontSize: 14, fontWeight: 700, color: tokens.textPrimary }}>{varies ? "~" : ""}{money(group.total)}</div>
          <button
            // A varying bill's amount is asked for one at a time, oldest first.
            onClick={() => (varies ? markPaid(oldest) : group.items.forEach(markPaid))}
            style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 12px", borderRadius: 999, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, backgroundColor: tokens.positiveBg, color: tokens.positive }}
          >
            <CheckCircle size={13} strokeWidth={2.6} /> {count > 1 && !varies ? `Mark all ${count} paid` : "Mark paid"}
          </button>
        </div>
      </div>
    );
  };

  const BillRow = ({ bill, note, first }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderTop: first ? "none" : `1px solid ${tokens.surfaceBorder}` }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: tokens.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{bill.payee}</div>
        <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 2 }}>{note}</div>
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontFamily: fonts.mono, fontSize: 14, fontWeight: 700, color: tokens.textPrimary }}>{billAmountText(bill, money)}</div>
        <button
          onClick={() => markPaid(bill)}
          style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 12px", borderRadius: 999, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, backgroundColor: tokens.positiveBg, color: tokens.positive }}
        >
          <CheckCircle size={13} strokeWidth={2.6} /> Mark paid
        </button>
      </div>
    </div>
  );

  return (
    <div>
      <p style={{ margin: "4px 4px 0", fontSize: 13, color: tokens.textMuted }}>{greeting()}{settings.profileName ? `, ${settings.profileName}` : ""}</p>

      <Card style={{ marginTop: 10, backgroundColor: tokens.topbarBg, border: "none" }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: tokens.topbarTextMuted }}>NET WORTH</div>
        <div style={{ margin: "6px 0 14px", fontFamily: fonts.display, fontSize: 32, fontWeight: 700, color: tokens.topbarText }}>
          {netWorth < 0 && !settings.maskAmounts ? "-" : ""}{money(Math.abs(netWorth))}
        </div>
        <div style={{ display: "flex", gap: 24 }}>
          <div>
            <div style={{ fontSize: 11, color: tokens.topbarTextMuted }}>Assets</div>
            <div style={{ fontFamily: fonts.mono, fontSize: 14, color: tokens.topbarText }}>{money(totalAssets)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: tokens.topbarTextMuted }}>Owed</div>
            <div style={{ fontFamily: fonts.mono, fontSize: 14, color: tokens.topbarText }}>{money(totalOwed)}</div>
          </div>
        </div>
      </Card>

      {lastMonth && (
        <Card style={{ marginTop: 12, backgroundColor: tokens.accentSoft, border: "none" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: tokens.accent }}>{lastMonth.label.toUpperCase()} IN NUMBERS</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 8 }}>
            {[
              ["Spent", money(lastMonth.spent), tokens.negative],
              ["Came in", money(lastMonth.received), tokens.positive],
              [lastMonth.saved >= 0 ? "Saved" : "Overspent", money(Math.abs(lastMonth.saved)), lastMonth.saved >= 0 ? tokens.positive : tokens.negative],
            ].map(([label, value, color]) => (
              <div key={label}>
                <div style={{ fontSize: 11, color: tokens.textMuted }}>{label}</div>
                <div style={{ fontFamily: fonts.mono, fontSize: 14, fontWeight: 700, color }}>{value}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 8, fontSize: 12, color: tokens.textMuted }}>{lastMonth.fixedPct}% of spending was fixed (EMIs, SIPs, rent, subscriptions).</div>
        </Card>
      )}

      <SectionTitle>This month</SectionTitle>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Card style={{ padding: 14 }}>
          <div style={{ fontSize: 12, color: tokens.textMuted }}>Money in</div>
          <div style={{ marginTop: 4, fontFamily: fonts.mono, fontSize: 17, fontWeight: 700, color: tokens.positive }}>{money(received)}</div>
          <div style={{ marginTop: 4, fontSize: 11, color: tokens.textMuted }}>{money(incoming)} still to come</div>
        </Card>
        <Card style={{ padding: 14 }}>
          <div style={{ fontSize: 12, color: tokens.textMuted }}>Money out</div>
          <div style={{ marginTop: 4, fontFamily: fonts.mono, fontSize: 17, fontWeight: 700, color: tokens.negative }}>{money(paidOut)}</div>
          <div style={{ marginTop: 4, fontSize: 11, color: tokens.textMuted }}>{money(toPay)} still to pay</div>
        </Card>
      </div>

      <SectionTitle right={<LinkButton icon={BarChart2} onClick={() => goTo("analysis")}>See details</LinkButton>}>
        Spending analysis
      </SectionTitle>
      <ExpenseAnalysis bills={bills} budgets={data.budgets} compact />

      {oneTimeBudgetsOf(data.budgets).length > 0 && (
        <>
          <SectionTitle right={<LinkButton icon={Target} onClick={() => goTo("accounts")}>Manage</LinkButton>}>Planned budgets</SectionTitle>
          <PlannedBudgets budgets={data.budgets} title="Saved so far" onOpen={() => goTo("accounts")} />
        </>
      )}

      {confirmations.length > 0 && (
        <>
          <SectionTitle>Did these autopays go through?</SectionTitle>
          <Card style={{ padding: "4px 16px" }}>
            {confirmations.map((b, i) => (
              <BillRow key={b.id} first={i === 0} bill={b} note={`Was due ${formatDate(b.dueDate)}`} />
            ))}
          </Card>
        </>
      )}

      <SectionTitle right={<LinkButton icon={List} onClick={() => goTo("bills")}>All bills</LinkButton>}>
        Due this week
      </SectionTitle>
      <Card style={{ padding: "4px 16px" }}>
        {dueGroups.length === 0 ? (
          <Empty>Nothing due in the next 7 days.</Empty>
        ) : (
          dueGroups.map((g, i) => <DueGroupRow key={g.key} first={i === 0} group={g} />)
        )}
      </Card>

      {owedToMe.length > 0 && (
        <>
          <SectionTitle right={<span style={{ fontFamily: fonts.mono, fontSize: 14, fontWeight: 700, color: tokens.textPrimary }}>{money(owedTotal)}</span>}>
            People owe me
          </SectionTitle>
          <Card style={{ padding: "4px 16px" }}>
            {owedToMe.map((b, i) => (
              <div key={b.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderTop: i ? `1px solid ${tokens.surfaceBorder}` : "none" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: tokens.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.payee}</div>
                  <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 2 }}>Lent {formatDate(b.dueDate)} · {daysSince(b.dueDate)}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontFamily: fonts.mono, fontSize: 14, fontWeight: 700, color: tokens.textPrimary }}>{money(b.amount)}</div>
                  <button
                    onClick={() => setSettling({ kind: "lent", entry: b })}
                    style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 12px", borderRadius: 999, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, backgroundColor: tokens.positiveBg, color: tokens.positive }}
                  >
                    <CheckCircle size={13} strokeWidth={2.6} /> Got it back
                  </button>
                </div>
              </div>
            ))}
          </Card>
        </>
      )}

      {iOwe.length > 0 && (
        <>
          <SectionTitle right={<span style={{ fontFamily: fonts.mono, fontSize: 14, fontWeight: 700, color: tokens.textPrimary }}>{money(iOweTotal)}</span>}>
            I owe
          </SectionTitle>
          <Card style={{ padding: "4px 16px" }}>
            {iOwe.map((i, n) => (
              <div key={i.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderTop: n ? `1px solid ${tokens.surfaceBorder}` : "none" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: tokens.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.source}</div>
                  <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 2 }}>Borrowed {formatDate(i.date)} · {daysSince(i.date)}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontFamily: fonts.mono, fontSize: 14, fontWeight: 700, color: tokens.textPrimary }}>{money(i.amount)}</div>
                  <button
                    onClick={() => setSettling({ kind: "borrowed", entry: i })}
                    style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 12px", borderRadius: 999, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, backgroundColor: tokens.pendingBg, color: tokens.pending }}
                  >
                    <CheckCircle size={13} strokeWidth={2.6} /> Paid it back
                  </button>
                </div>
              </div>
            ))}
          </Card>
        </>
      )}

      {settling && <SettleSheet kind={settling.kind} entry={settling.entry} data={data} onClose={() => setSettling(null)} />}
      {payBill.sheet}

      {cards.length > 0 && (
        <>
          <SectionTitle>Credit cards</SectionTitle>
          <Card>
            {cards.map((c, i) => {
              const used = c.cardLimit > 0 ? Math.min(100, (c.balance / c.cardLimit) * 100) : 0;
              return (
                <div key={c.id} style={{ marginTop: i ? 16 : 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 700, color: tokens.textPrimary }}>
                    <span>{c.name}</span>
                    <span style={{ fontFamily: fonts.mono }}>{money(c.balance)}</span>
                  </div>
                  <div style={{ height: 6, borderRadius: 3, backgroundColor: tokens.neutralSoft, margin: "8px 0 6px", overflow: "hidden" }}>
                    <div style={{ width: `${used}%`, height: "100%", backgroundColor: used > 70 ? tokens.negative : tokens.accent }} />
                  </div>
                  <div style={{ fontSize: 12, color: tokens.textMuted }}>
                    {money(c.cardLimit - c.balance)} available of {money(c.cardLimit)} · bill day {c.billingDay}
                  </div>
                </div>
              );
            })}
          </Card>
        </>
      )}

      {loans.length > 0 && (
        <>
          <SectionTitle>Loans</SectionTitle>
          <Card>
            {loans.map((l, i) => (
              <div key={l.id} style={{ display: "flex", justifyContent: "space-between", marginTop: i ? 14 : 0 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: tokens.textPrimary }}>{l.name}</div>
                  <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 2 }}>
                    {money(l.monthlyPayment)} / month{payoffText(l, money) ? ` · ${payoffText(l, money)}` : ""}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontFamily: fonts.mono, fontSize: 14, fontWeight: 700, color: tokens.textPrimary }}>{money(l.remainingBalance)}</div>
                  <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 2 }}>remaining</div>
                </div>
              </div>
            ))}
          </Card>
        </>
      )}

      {bills.length === 0 && income.length === 0 && (
        <Empty>No data yet. Tap the + button below to add your first expense, income, bill or task.</Empty>
      )}
    </div>
  );
};

export default HomeScreen;
